import Link from "next/link"
import Image from "next/image"
import { Button } from "@/components/ui/button"
import { ArrowRight, Drill, Cable, Zap } from "lucide-react"

export function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-background">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <Image
          src="/images/hero-hdd-drilling.jpg"
          alt="Horizontal Directional Drilling equipment in action"
          fill
          className="object-cover opacity-50"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/70 to-background" />
      </div>
      
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e3a5f10_1px,transparent_1px),linear-gradient(to_bottom,#1e3a5f10_1px,transparent_1px)] bg-[size:4rem_4rem]" />
      
      <div className="container relative mx-auto px-4 py-20 md:px-6 md:py-28 lg:py-36">
        <div className="mx-auto max-w-4xl text-center">
          {/* Badge */}
          <div className="mb-6 inline-flex items-center rounded-full border border-primary/20 bg-primary/10 px-4 py-1.5">
            <span className="text-sm font-medium text-primary">Trusted Network Infrastructure Partner</span>
          </div>
          
          {/* Main Heading */}
          <h1 className="mb-6 text-balance text-4xl font-bold tracking-tight text-foreground md:text-5xl lg:text-6xl">
            Building the{" "}
            <span className="text-primary">Underground Networks</span>{" "}
            That Power Your World
          </h1>
          
          {/* Subheading */}
          <p className="mx-auto mb-8 max-w-2xl text-pretty text-lg text-muted-foreground md:text-xl">
            From Horizontal Directional Drilling to Fiber Optic Pulling, World Connections LLC delivers precision infrastructure solutions across Michigan and beyond.
          </p>
          
          {/* CTA Buttons */}
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link href="/contact">
                Request a Quote
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-primary/30 text-foreground hover:bg-primary/10 bg-transparent">
              <Link href="/about">Learn About Us</Link>
            </Button>
          </div>
        </div>
        
        {/* Feature Cards */}
        <div className="mt-16 grid gap-6 md:grid-cols-3">
          <div className="group rounded-xl border border-border/50 bg-card p-6 transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Drill className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">Directional Drilling</h3>
            <p className="text-sm text-muted-foreground">
              Precision HDD services for minimal surface disruption and maximum efficiency.
            </p>
          </div>
          
          <div className="group rounded-xl border border-border/50 bg-card p-6 transition-all hover:border-accent/30 hover:shadow-lg hover:shadow-accent/5">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-accent/10">
              <Cable className="h-6 w-6 text-accent" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">Fiber Pulling</h3>
            <p className="text-sm text-muted-foreground">
              Complete fiber optic solutions from pulling to splicing and testing.
            </p>
          </div>
          
          <div className="group rounded-xl border border-border/50 bg-card p-6 transition-all hover:border-primary/30 hover:shadow-lg hover:shadow-primary/5">
            <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
              <Zap className="h-6 w-6 text-primary" />
            </div>
            <h3 className="mb-2 text-lg font-semibold text-foreground">Fast Deployment</h3>
            <p className="text-sm text-muted-foreground">
              Quick turnaround with multiple teams ready for projects of greater scale.
            </p>
          </div>
        </div>
      </div>
    </section>
  )
}
